import { useEffect, useRef, useState } from "react";

const stats = [
  { value: "2", label: "Flagship Products" },
  { value: "100%", label: "iOS Native" },
];

const values = [
  {
    icon: "ri-shield-check-line",
    title: "Security First",
    desc: "Every product is built with security as the foundation, not an afterthought.",
  },
  {
    icon: "ri-speed-up-line",
    title: "Performance",
    desc: "Optimized code and smart architecture ensure smooth, fast experiences.",
  },
  {
    icon: "ri-user-heart-line",
    title: "User-Centric",
    desc: "Intuitive interfaces that feel natural and effortless to every user.",
  },
];

export default function AboutSection() {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="about" className="py-28 md:py-36" style={{ background: "#f7f8fb" }}>
      <div className="max-w-6xl mx-auto px-6 md:px-10" ref={sectionRef}>
        {/* Top: text + image */}
        <div className="flex flex-col lg:flex-row items-center gap-16 mb-20">
          {/* Left text */}
          <div
            className="flex-1"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-40px)",
              transition: "opacity 0.7s ease 0.1s, transform 0.7s ease 0.1s",
            }}
          >
            <span className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-4 block">
              Who We Are
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              A Team of
              <br />
              <span className="text-transparent bg-clip-text"
                style={{ backgroundImage: "linear-gradient(90deg, #0f3460, #00b894)" }}>
                iOS Experts
              </span>
            </h2>
            <p className="text-gray-600 text-lg leading-relaxed mb-6 max-w-lg">
              <strong>SUDARC SRL</strong> is a dedicated team of experts developing modern applications for the iOS platform. We combine deep technical knowledge with a passion for elegant design to create products that truly make a difference.
            </p>
            <p className="text-gray-500 text-base leading-relaxed max-w-lg">
              Our mission is simple: enhance user experience and improve the security of mobile devices. From concept to App Store, we handle every step with precision and care — building tools that millions of users rely on every day.
            </p>
          </div>

          {/* Right: abstract geometric composition */}
          <div
            className="flex-1 flex items-center justify-center relative"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(40px)",
              transition: "opacity 0.7s ease 0.2s, transform 0.7s ease 0.2s",
              minHeight: 360,
            }}
          >
            <div className="relative w-72 h-80">
              <div className="absolute inset-0 rounded-3xl"
                style={{ background: "linear-gradient(135deg, rgba(0,184,148,0.18) 0%, rgba(0,184,148,0.05) 100%)", transform: "rotate(-6deg)" }} />
              <div className="absolute inset-4 rounded-3xl"
                style={{ background: "linear-gradient(135deg, rgba(15,52,96,0.14) 0%, rgba(15,52,96,0.04) 100%)", transform: "rotate(2deg)" }} />
              <div className="absolute inset-8 rounded-3xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/eee2778ea1d81088b1f65ca497e0fb01.jpg"
                  alt="SUDARC SRL team working on iOS applications"
                  className="w-full h-full object-cover object-top rounded-2xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Stats row */}
        <div
          className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-20"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease 0.3s, transform 0.7s ease 0.3s",
          }}
        >
          {stats.map((stat) => (
            <div key={stat.label} className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-gray-900 mb-2"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {stat.value}
              </div>
              <div className="text-xs font-semibold tracking-widest uppercase text-gray-400">
                {stat.label}
              </div>
            </div>
          ))}
        </div>

        {/* Values row */}
        <div
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
          style={{
            opacity: visible ? 1 : 0,
            transform: visible ? "translateY(0)" : "translateY(30px)",
            transition: "opacity 0.7s ease 0.4s, transform 0.7s ease 0.4s",
          }}
        >
          {values.map((v, i) => (
            <div
              key={v.title}
              className="bg-white rounded-2xl p-8"
              style={{ transitionDelay: `${0.4 + i * 0.1}s` }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl mb-5"
                style={{ background: "linear-gradient(135deg, #f0f4ff 0%, #e8f8f3 100%)" }}>
                <i className={`${v.icon} text-xl`} style={{ color: "#0f3460" }} />
              </div>
              <h4 className="text-lg font-bold text-gray-900 mb-3"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {v.title}
              </h4>
              <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
